/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 *
 * TdChallenge — first-stage email-entry form. User submits an email; the
 * backend mails a 6-digit access code. Caller wires the form's submit
 * handler to `authService.formChallenge`.
 *
 * Lit light-DOM component. The `alreadyRendered` latch from the
 * vanilla version is gone: Lit's `@event` bindings live in the
 * template and are re-attached on every render, so the latch had
 * nothing to defend against anymore. External listeners wired via
 * `getForm()`/`getErrorMessage()` survive Lit renders because Lit's
 * reconciler keeps the underlying DOM nodes when the template shape
 * doesn't change.
 */

import { LitElement, html, type TemplateResult } from 'lit';
import { customElement } from 'lit/decorators.js';
import { t, LocaleController } from '../services/i18n.js';

@customElement('td-challenge')
export class TdChallenge extends LitElement {
  /** Re-renders this component when the UI locale changes (#89). */
  readonly localeController = new LocaleController(this);

  override createRenderRoot(): HTMLElement {
    return this;
  }

  override render(): TemplateResult {
    return html`
      <div class="bd-login-shell">
        <header class="bd-login-header wa-split">
          <a class="bd-logo" href="https://www.beyonddemise.com/">
            <img
              src="/assets/images/LogoWithTextTransparent.png"
              alt="Beyond Demise"
              title="Beyond Demise®"
            />
          </a>
          <a href="https://www.beyonddemise.com/">
            ${t('login.backToWebsite')}
          </a>
        </header>
        <div class="login-container">
          <wa-card class="card-header" appearance="accent">
            <div slot="header" class="wa-split">
              <h1>Beyond Demise</h1>
            </div>

            <div slot="footer" class="wa-split">
              <!-- role="alert" announces dynamically injected auth errors
                   to screen readers the moment [hidden] is removed (#243) -->
              <div
                id="error-message"
                class="error-message"
                role="alert"
                hidden
              ></div>
            </div>
            <p>${t('login.tagline')}</p>
            <p>${t('challenge.sendCodeInfo')}</p>

            <form id="challenge-form" class="challenge-form">
              <wa-input
                type="email"
                name="email"
                id="email"
                required
                placeholder=${t('challenge.emailPlaceholder')}
                label=${t('challenge.emailLabel')}
              ></wa-input>
              <div class="wa-gap-xl">
                <wa-button type="submit" size="m" variant="brand">
                  ${t('challenge.sendCode')}
                </wa-button>
              </div>
            </form>
          </wa-card>
        </div>
      </div>
    `;
  }

  /** Returns the challenge form element, or `null` if not yet rendered. */
  getForm(): HTMLFormElement | null {
    return this.querySelector<HTMLFormElement>('#challenge-form');
  }

  /** Returns the error message host, or `null` if not yet rendered. */
  getErrorMessage(): HTMLElement | null {
    return this.querySelector<HTMLElement>('#error-message');
  }
}
